import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { LanguageContext } from '../hooks/useLanguage';
import { nav } from '../data/content';
import { SHOW } from '../config/visibility';

const LogoIcon = () => (
  <svg viewBox="0 0 20 58" width="16" height="52" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="boltGradF" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#D4AE5C" />
        <stop offset="100%" stopColor="#A8893A" />
      </linearGradient>
    </defs>
    <polygon points="18,2 6,2 3,26 15,26" fill="url(#boltGradF)" />
    <polygon points="7,26 19,26 16,50 4,50" fill="url(#boltGradF)" />
  </svg>
);

const Footer: React.FC = () => {
  const { lang } = useContext(LanguageContext);
  const menu = nav[lang];

  const socials = [
    {
      name: 'Instagram', href: '#',
      svg: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="20" rx="5"/>
          <circle cx="12" cy="12" r="4"/>
          <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
        </svg>
      ),
    },
    {
      name: 'LinkedIn', href: '#',
      svg: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
          <circle cx="4" cy="4" r="2"/>
        </svg>
      ),
    },
    {
      name: 'YouTube', href: '#',
      svg: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/>
        </svg>
      ),
    },
    {
      name: 'WhatsApp', href: 'https://wa.me/34936943568',
      svg: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
          <path d="M12 0C5.373 0 0 5.373 0 12c0 2.122.554 4.112 1.524 5.837L.057 23.22a.75.75 0 00.922.922l5.383-1.467A11.944 11.944 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.725 9.725 0 01-4.963-1.357l-.357-.213-3.697 1.008 1.008-3.697-.213-.357A9.725 9.725 0 012.25 12c0-5.376 4.374-9.75 9.75-9.75S21.75 6.624 21.75 12 17.376 21.75 12 21.75z"/>
        </svg>
      ),
    },
  ];

  const serviceLinks = [
    { label: lang === 'es' ? 'Diseño KNX' : lang === 'en' ? 'KNX System Design' : 'Conception KNX', href: '#services' },
    { label: lang === 'es' ? 'Iluminación' : lang === 'en' ? 'Lighting' : 'Éclairage', href: '#iluminacion' },
    { label: lang === 'es' ? 'Persianas' : lang === 'en' ? 'Blinds' : 'Volets', href: '#persianas' },
    { label: lang === 'es' ? 'Seguridad' : lang === 'en' ? 'Security' : 'Sécurité', href: '#seguridad' },
    ...(SHOW.electricidad ? [{ label: menu.electricista, href: '#electricidad' }] : []),
    ...(SHOW.vatiocentral ? [{ label: 'Vatio Central', href: '#vatiocentral' }] : []),
  ];

  const tagline = lang === 'es'
    ? 'Diseño Eléctrico · Automatización · España'
    : lang === 'en'
    ? 'Electrical Design · Automation · Spain'
    : 'Conception Électrique · Domotique · Automatisation · Espagne';

  return (
    <footer style={{ background: '#0D0D0D', color: '#fff', padding: 'clamp(60px, 8vw, 80px) 0 40px' }}>
      {/* Gold top border */}
      <div style={{ height: '3px', background: 'linear-gradient(90deg, #C8A96E, #E8C98A)', marginBottom: '0' }} />

      <div className="container" style={{ paddingTop: '64px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr',
            gap: '48px',
            paddingBottom: '64px',
            borderBottom: '1px solid rgba(200,169,110,0.15)',
          }}
          className="footer-grid"
        >
          {/* Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <LogoIcon />
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '13px', textTransform: 'uppercase' }}>
                  <span style={{ fontWeight: 700, letterSpacing: '4px', color: '#FFFFFF' }}>VATIO</span>
                  {' '}
                  <span style={{ fontWeight: 400, letterSpacing: '4px', color: 'rgba(255,255,255,0.4)' }}>CENTRAL</span>
                </div>
                <div style={{ width: '20px', height: '1px', background: '#C8A96E', margin: '6px 0' }} />
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '7.5px',
                  fontWeight: 300,
                  letterSpacing: '2px',
                  color: '#333',
                  textTransform: 'uppercase',
                }}>
                  Electrical Design · Automation
                </div>
              </div>
            </div>

            <p style={{
              fontFamily: 'var(--font-body)',
              color: '#555555',
              fontSize: '14px',
              lineHeight: 1.8,
              maxWidth: '280px',
              marginBottom: '32px',
            }}>
              {tagline}
            </p>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' as const }}>
              {socials.map((s) => (
                <motion.a
                  key={s.name}
                  href={s.href}
                  target={s.href.startsWith('http') ? '_blank' : undefined}
                  rel="noreferrer"
                  whileHover={{ color: '#C8A96E', borderColor: 'rgba(200,169,110,0.4)' }}
                  style={{
                    width: '36px', height: '36px',
                    border: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: '#555555',
                    transition: 'all 0.3s',
                  }}
                  title={s.name}
                >
                  {s.svg}
                </motion.a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 style={{
              fontFamily: 'var(--font-body)',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '3px',
              textTransform: 'uppercase' as const,
              color: '#C8A96E',
              marginBottom: '24px',
            }}>
              {lang === 'es' ? 'Servicios' : lang === 'en' ? 'Services' : 'Services'}
            </h4>
            {serviceLinks.map((item) => (
              <a
                key={item.label}
                href={item.href}
                style={{
                  display: 'block',
                  fontFamily: 'var(--font-body)',
                  color: '#555555',
                  fontSize: '14px',
                  marginBottom: '12px',
                  transition: 'color 0.2s',
                  letterSpacing: '0.5px',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#AAAAAA')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#555555')}
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* Contact */}
          <div>
            <h4 style={{
              fontFamily: 'var(--font-body)',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '3px',
              textTransform: 'uppercase' as const,
              color: '#C8A96E',
              marginBottom: '24px',
            }}>
              {menu.contact}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <a
                href="mailto:info@vatiocentral.com"
                style={{ fontFamily: 'var(--font-body)', color: '#555555', fontSize: '14px', transition: 'color 0.2s' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#AAAAAA')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#555555')}
              >
                info@vatiocentral.com
              </a>
              <span style={{ fontFamily: 'var(--font-body)', color: '#555555', fontSize: '14px' }}>
                España
              </span>
            </div>
          </div>
        </div>

        <div style={{
          paddingTop: '32px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap' as const, gap: '24px',
        }}>
          <p style={{ fontFamily: 'var(--font-body)', color: '#555555', fontSize: '12px', letterSpacing: '0.5px' }}>
            © 2026 Vatio Central.{' '}
            {lang === 'es' ? 'Todos los derechos reservados.' : lang === 'en' ? 'All rights reserved.' : 'Tous droits réservés.'}
          </p>

          {/* KNX Partner Badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 24px',
            border: '1px solid rgba(200,169,110,0.2)',
            background: 'rgba(200,169,110,0.03)',
          }}>
            <div style={{
              background: '#fff',
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <img
                src="/Partner.jpg"
                alt="KNX Partner Certified"
                style={{ height: '36px', width: 'auto', display: 'block' }}
              />
            </div>
            <div style={{ borderLeft: '1px solid rgba(200,169,110,0.2)', paddingLeft: '16px' }}>
              <div style={{
                fontFamily: 'var(--font-body)',
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '2px',
                color: '#C8A96E',
                textTransform: 'uppercase' as const,
              }}>
                {lang === 'es' ? 'Certificado Oficial' : lang === 'en' ? 'Officially Certified' : 'Certifié Officiel'}
              </div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: '10px', color: '#555555', marginTop: '3px', letterSpacing: '1px' }}>
                KNX PARTNER
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 768px) { .footer-grid { grid-template-columns: 1fr 1fr !important; } }
        @media (max-width: 480px) { .footer-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </footer>
  );
};

export default Footer;
